<template>
  <div
    class="p-reviews__reply"
    :class="{'is-edit': isEditable}"
  >
    <div class="p-reviews__reply__header">
      <div class="p-reviews__reply__name">
        {{ $t('pages.CompaniesReviews.replyContent') }}
      </div>
      <div
        v-if="providedFeaturesReplyTemplate && !autoGenerateReplyLoading"
        ref="template"
        class="p-reviews__reply__template"
      >
        <div class="replyOptionButtons">
          <button
            v-show="isEditable"
            type="button"
            class="p-reviews__reply__template__button"
            @click="callReplayTemplates(reviewUuid)"
          >
            <span class="p-reviews__reply__template__button__icon">
              <KkIcon name="add" color="#E9446A" />
            </span>
            <span class="p-reviews__reply__template__button__label">
              {{ $t('pages.CompaniesReviews.replyTemplate.select') }}
            </span>
          </button>
          <button
            v-if="providedFeaturesAutoGenerateReply && isEditable"
            type="button"
            class="p-reviews__reply__template__button autoReplyButton"
            @click="generateAutoReply"
          >
            <span class="p-reviews__reply__template__button__icon">
              <KkIcon name="pencilUnderLine" color="#E9446A" />
            </span>
            <span class="p-reviews__reply__template__button__label">
              {{ $t('pages.CompaniesReviews.autoGenerateReply') }}
            </span>
          </button>
        </div>
        <div v-if="showTemplates" class="p-reviews__reply__template__content">
          <KkLoading v-if="templatesLoading" min-height="120px" />
          <template v-else>
            <div class="p-reviews__reply__template__content__search">
              <span>
                <KkIcon name="search" color="#3A3B3F" />
              </span>
              <input v-model="templateFilter" type="text">
            </div>
            <div v-if="ownTemplates.length" class="p-reviews__reply__template__content__items">
              <button
                v-for="template in ownTemplates"
                :key="template.id"
                type="button"
                class="p-reviews__reply__template__content__item"
                @click="selectTemplate(template.content)"
              >
                {{ template.name }}
              </button>
            </div>
            <p
              v-else
              class="p-reviews__reply__template__content__noData"
            >
              {{ $t('pages.CompaniesReviews.replyTemplate.noData') }}
            </p>
          </template>
        </div>
      </div>
    </div>
    <div
      v-if="loading || autoGenerateReplyLoading"
      class="p-box"
    >
      <div class="p-box__body">
        <Loading
          min-height="200px"
        />
      </div>
    </div>
    <template v-else>
      <div class="p-reviews__reply__field">
        <textarea
          ref="textarea"
          v-model="content"
          class="c-textArea"
          :disabled="!isEditable"
          autofocus
          rows="10"
        />
      </div>
      <template v-if="isEditable">
        <div class="p-reviews__reply__note">
          <i18n-t v-if="review.attributes.site_type === 'tabelog'" keypath="pages.CompaniesReviews.replyTabelog.description" tag="span">
            <a :href="tabelogPolicyPdfPath" target="_blank">{{ $t('pages.CompaniesReviews.replyTabelog.policy') }}</a>
          </i18n-t>
          <span v-else>
            {{ $t('pages.CompaniesReviews.replyDescription') }}
          </span>
        </div>
        <div v-show="errors.length > 0" class="p-reviews__reply__note">
          <span
            v-for="error in errors" :key="error"
          >{{ error }}</span>
        </div>
      </template>
      <div class="approvalHistory">
        <KkApprovalHistory
          v-if="showApprovalHistory"
          height="200px"
          :approval-notes="approvalNotes"
        />
      </div>
      <div
        v-if="isEditable"
        class="c-button c-button--right"
      >
        <KkButton
          class="c-button__item"
          color="secondary"
          :text="cancelButtonName"
          @click="handleCancelButton"
        />
        <KkButton
          :disabled="content.length === 0"
          class="c-button__item"
          color="primary"
          :text="submitButtonName"
          @click="handleSubmitButton"
        />
      </div>
      <template v-else>
        <div class="p-reviews__reply__edit">
          <span v-if="hasReplyerName">
            {{ $t('pages.CompaniesReviews.repliedBy', {date: postedAt, name: replyerName}) }}
          </span>
          <span v-else>
            {{ $t('pages.CompaniesReviews.repliedAt', {date: postedAt}) }}
          </span>
          <span v-if="isVisibleReply" class="p-reviews__reply__edit_link" @click="makeEditable">
            {{ $t('pages.CompaniesReviews.editReply') }}
          </span>
        </div>
        <div v-if="showApprovalHistoryModalLink" class="p-reviews__reply__approval">
          <span>
            ({{ $t('pages.CompaniesReviews.approvedBy', {name: approverName}) }}
            <span class="p-reviews__reply__edit_link" @click="showApprovalHistoryPreview">
              {{ $t('pages.CompaniesReviews.seeApprovalHistory') }}
            </span>
            )
          </span>
        </div>
      </template>
    </template>
    <KkApprovalHistoryModal
      :type="modalType"
      :is-visible="showRejectModal"
      :loading="loading"
      :approval-notes="approvalNotes"
      @close="showRejectModal = false"
      @submit="reject"
    />
    <KkApprovalHistoryModal
      :type="modalType"
      :is-visible="showApplyAndApproveModal"
      :loading="loading"
      :approval-notes="approvalNotes"
      @close="showApplyAndApproveModal = false"
      @submit="handleSubmitApprovalNote"
    />
  </div>
</template>

<script>
import Loading from '../../components/Loading.vue'
import { KkApprovalHistory, KkApprovalHistoryModal } from '../../domains/review'
import { KkIcon, KkLoading, KkButton } from '../../components/base'
import { mapState, mapGetters, mapActions } from 'vuex'
import { actionEvent } from '../../modules/googleAnalytics.ts'
import tabelogPolicyPdfPath from '../../../../pdf/tabelog_policy.pdf'

export default {
  name: 'Reply',
  components: {
    Loading,
    KkIcon,
    KkLoading,
    KkButton,
    KkApprovalHistoryModal,
    KkApprovalHistory
  },
  props: {
    review: {
      type: Object,
      required: true,
    },
    initialContent: {
      type: String,
      default: ''
    },
    initialPostedAt: {
      type: String,
      default: ''
    },
    initialReplyerName: {
      type: String,
      default: ''
    },
    canReply: {
      type: Boolean,
      required: true,
    },
    approverName: {
      type: String,
      required: false
    },
    approvalStatus: {
      type: String,
      required: false
    },
    approvalNotes: {
      type: Array,
      default: () => []
    },
    reviewsRequestParamsForApply: {
      type: Object,
      required: true
    },
  },
  emits: ['close-reply', 'data-refresh'],
  data: function() {
    return {
      content: '',
      postedAt: '',
      replyerName: '',
      loading: false,
      isEditable: false,
      isReplied: false,
      hasReplyerName: false,
      errors: [],
      showTemplates: false,
      templateFilter: '',
      templateLoaded: false,
      showRejectModal: false,
      showApplyAndApproveModal: false,
      modalType: '',
      autoGenerateReplyLoading: false,
    }
  },
  computed: {
    ...mapState({
      templates: state => state.companiesReplyTemplates.templatesForReviews,
      templatesLoading: state => state.companiesReplyTemplates.loading,
      autoGenerateReply: state => state.companiesReviews.autoGenerateReply,
    }),
    ...mapGetters({
      providedFeaturesReplyTemplate: 'companiesCompany/providedFeaturesReplyTemplate',
      providedFeaturesAutoGenerateReply: 'companiesCompany/providedFeaturesAutoGenerateReply',
      replyApprovalFlow: 'companiesCompany/replyApprovalFlow',
      showOnlyOwnerAuthority: 'companiesAccessibleApi/showOnlyOwnerAuthority',
      showOnlyEditorOrOperatorAuthority: 'companiesAccessibleApi/showOnlyEditorOrOperatorAuthority',
    }),
    reviewUuid() {
      return this.review.attributes.review_uuid
    },
    ownTemplates() {
      const templates = this.templates[this.reviewUuid]
      if (!templates) return []
      return templates.data.filter(template => (template.name.indexOf(this.templateFilter) !== -1))
    },
    underRequesting() { return this.approvalStatus === 'requesting' },
    underRejecting() { return this.approvalStatus === 'rejected' },
    approved() { return this.approvalStatus === 'approved' },
    approvalInitialState() {
      // 承認機能がONだが、何もアクションが行われていない状態
      return this.replyApprovalFlow && !this.approvalStatus
    },
    showApprovalHistoryModalLink() {
      // 承認機能ONかつ承認後（返信後）
      return this.replyApprovalFlow && this.approved
    },
    showApprovalHistory() {
      if (!this.replyApprovalFlow) return false // 申請機能offの場合は非表示
      // 申請者・オーナーも「申請中」と「却下中」のみ表示
      return this.underRequesting || this.underRejecting
    },
    submitButtonName() {
      if (!this.replyApprovalFlow) return this.$t('pages.CompaniesReviews.submitReply')
      // オーナーの場合、申請中は「承認する」。それ以外は「返信する」
      if (this.showOnlyOwnerAuthority) {
        if (this.underRequesting) return this.$t('pages.CompaniesReviews.approveAndPublish')
        else return this.$t('pages.CompaniesReviews.submitReply')
      }
      // 申請者の場合、承認機能がONなら「申請中」に
      else {
        return this.$t('pages.CompaniesReviews.apply')
      }
    },
    cancelButtonName() {
      if (!this.replyApprovalFlow || this.approvalInitialState) return this.$t('labels.cancel')
      // オーナーの場合、申請中は「却下」。それ以外は「キャンセル」
      if (this.showOnlyOwnerAuthority) {
        if (this.underRequesting) return this.$t('pages.CompaniesReviews.reject')
        else return this.$t('labels.cancel')
      }
      // 申請者の場合、申請中,申請却下後は「申請取り消し」。それ以外は「キャンセル」
      else  {
        if (this.approvalInitialState || this.underRequesting || this.underRejecting) return this.$t('pages.CompaniesReviews.revert')
        else return this.$t('labels.cancel')
      }
    },
    isVisibleReply() {
      // 承認機能がONになっている場合は、オーナーが「却下中」は表示しない
      if (this.replyApprovalFlow) {
        if (this.showOnlyOwnerAuthority && this.underRejecting) return false
      }
      return this.canReply
    },
    tabelogPolicyPdfPath() {
      return tabelogPolicyPdfPath
    },
  },
  mounted () {
    window.addEventListener('click', this.hideTemplate = e => {
      if (this.$refs.template.contains(e.target)) return
      this.showTemplates = false
    })
    this.content = this.initialContent
    this.postedAt = this.initialPostedAt
    this.replyerName = this.initialReplyerName
    if (this.initialContent === '') {
      this.isEditable = true
    } else {
      this.isReplied = true
    }
    if (this.initialReplyerName !== '') this.hasReplyerName = true

    if (this.replyApprovalFlow) {
      // オーナーの場合、「申請中」のみ編集可能
      if (this.showOnlyOwnerAuthority && this.underRequesting) this.isEditable = true
      // 申請者の場合、「申請中」「差し戻し後」は編集可能
      if (this.showOnlyEditorOrOperatorAuthority && (this.underRequesting || this.underRejecting)) this.isEditable = true
    }
  },
  beforeUnmount() {
    window.removeEventListener('click', this.hideTemplate)
  },
  methods: {
    ...mapActions({
      submitReply: 'companiesReviews/submitReply',
      readReplyTemplatesForReview: 'companiesReplyTemplates/readReplyTemplatesForReview',
      applyReview: 'companiesReviews/apply',
      approveReview: 'companiesReviews/approve',
      rejectReview: 'companiesReviews/reject',
      revertReview: 'companiesReviews/revert',
      getAutoGenerateReply: 'companiesReviews/getAutoGenerateReply',
      createAutoGenerateReply: 'companiesReviews/createAutoGenerateReply',
    }),
    makeEditable: function () {
      this.isEditable = true
    },
    handleCancelButton() {
      actionEvent('CancelReply')
      if (!this.replyApprovalFlow || this.approvalInitialState) return this.makeUneditable()
      // オーナーの場合、申請中は「却下」用モーダル表示。それ以外は「キャンセル」
      if (this.showOnlyOwnerAuthority) {
        if (this.underRequesting) this.showRejectModal = true
        else this.makeUneditable()
      }
      // 申請者の場合、申請中,却下後は「申請取り消し」用モーダル表示。それ以外は「キャンセル」
      else  {
        if (this.approvalInitialState || this.underRequesting || this.underRejecting) return this.revert()
        else this.makeUneditable()
      }
    },
    handleSubmitButton() {
      if (!this.replyApprovalFlow) return this.reply()

      // オーナーの場合、申請中は「承認する」。それ以外は「返信する」
      if (this.showOnlyOwnerAuthority) {
        if (this.underRequesting) this.openApprovalHistoryModal()
        else this.reply()
      }
      // 申請者の場合「申請する」ボタン。
      else {
        this.openApprovalHistoryModal()
      }
    },
    makeUneditable: function () {
      this.isEditable = false
      this.content = this.initialContent
      if (!this.isReplied) this.$emit('close-reply')
    },
    showApprovalHistoryPreview() {
      this.modalType = 'preview'
      this.showApplyAndApproveModal = true
    },
    openApprovalHistoryModal: function () {
      this.modalType = this.showOnlyOwnerAuthority ? 'approve' : 'apply'
      this.showApplyAndApproveModal = true
    },
    handleSubmitApprovalNote(comment) {
      // オーナー→「承認する」。申請者→「申請する」
      if (this.showOnlyOwnerAuthority) {
        this.approve(comment)
      }
      else {
        this.apply(comment)
      }
    },
    reply() {
      if (this.content === '') return

      this.loading = true
      this.submitReply({
        company_uuid: this.$route.params.company_uuid,
        review_uuid: this.reviewUuid,
        payload: {
          review_reply: {
            content: this.content,
          },
          successFn: (res) => {
            this.loading = false
            if (res.data.errors) {
              this.errors = res.data.errors
            } else {
              this.isEditable = false
              this.isReplied = true
              this.hasReplyerName = true
              this.content = res.data.data.attributes.content
              this.postedAt = res.data.data.attributes.posted_at
              this.replyerName = res.data.data.attributes.replyer_name
              actionEvent('ReviewReply')
            }
          },
          failureFn: () => this.loading = false,
        },
      })
    },
    async apply(comment) {
      const isSuccess = await this.applyReview({
        company_uuid: this.$route.params.company_uuid,
        review_uuid: this.reviewUuid,
        payload: {
          review_reply: {
            content: this.content,
          },
          approval_request: {
            comment: comment,
          },
          ...this.reviewsRequestParamsForApply,
        }
      })
      if (isSuccess) {
        this.showApplyAndApproveModal = false
        this.$emit('data-refresh')
      }
    },
    async approve(comment) {
      const isSuccess = await this.approveReview({
        company_uuid: this.$route.params.company_uuid,
        review_uuid: this.reviewUuid,
        payload: {
          comment: comment,
          reviewReply: {
            content: this.content
          }
        }
      })
      if (isSuccess) {
        this.showApplyAndApproveModal = false
        this.$emit('data-refresh')
      }
    },
    async revert() {
      if (confirm(this.$t('pages.CompaniesReviews.confirmToRevert'))) {
        const isSuccess = await this.revertReview({
          company_uuid: this.$route.params.company_uuid,
          review_uuid: this.reviewUuid,
        })
        if (isSuccess) {
          this.$emit('data-refresh')
        }
      }
    },
    async reject(comment) {
      const isSuccess = await this.rejectReview({
        company_uuid: this.$route.params.company_uuid,
        review_uuid: this.reviewUuid,
        payload: {
          comment: comment,
        }
      })

      if (isSuccess) {
        this.showRejectModal = false
        this.$emit('data-refresh')
      }
    },
    callReplayTemplates(reviewUuid) {
      actionEvent('ReplyTemplate')
      if (this.templateLoaded === false) {
        this.readReplyTemplatesForReview(reviewUuid)
        this.templateLoaded = true
      }
      this.showTemplates = true
    },
    selectTemplate(template) {
      const pos = this.$refs.textarea.selectionStart
      const before = this.content.substring(0, pos)
      const after = this.content.substring(pos, this.content.length)
      this.content = before + template + after
      this.showTemplates = false
    },
    async generateAutoReply() {
      actionEvent('AIReplySupport')
      this.showTemplates = false
      this.autoGenerateReplyLoading = true
      const isSuccessCreate = await this.createAutoGenerateReply({
        companyUuid: this.$route.params.company_uuid,
        reviewUuid: this.reviewUuid,
      })
      if(!isSuccessCreate) return

      const isSuccessFetch = await this.getAutoGenerateReply({
        companyUuid: this.$route.params.company_uuid,
        reviewUuid: this.reviewUuid,
      })
      if(isSuccessFetch) this.content = this.autoGenerateReply
      this.autoGenerateReplyLoading = false
    },
  },
}
</script>
<style scoped lang="scss">
.approvalHistory {
  margin-top: 24px;
}
.autoReplyButton {
  margin-left: 16px;
}
.replyOptionButtons {
  display: flex;
}
</style>
